import type { ServiceRequest } from '@medplum/fhirtypes';
import React from 'react';

import { Stack, Tag, Text } from '@eluve/components';
import {
  type FhirPageControls,
  type FhirSectionStatus,
  LoadMoreRow,
  SectionBoundary,
} from '@eluve/fhir-data';
import { formatFhirDate } from '@eluve/fhir-renderer';

import { isPendingReview } from '../review-markers';
import { todoDueDate } from '../todo-list';

/**
 * The Overview-tab Patient to-dos card (ENG-5368): the patient's
 * home-program ServiceRequests written by Care Plan activation. Deliberately
 * read-only — no check-off until the adherence model lands (ENG-5382).
 * Callers pass the already-filtered visible list (see todo-list.ts).
 */
export type TodosCardProps = {
  todos: ServiceRequest[];
  status: FhirSectionStatus;
  paging: FhirPageControls;
  onOpen: (serviceRequestId: string) => void;
};

const PendingDot: React.FC = () => (
  <span
    aria-hidden
    className="size-1.75 rounded-full border-[1.5px] bg-bg-primary"
    style={{ borderColor: 'var(--color-text-insights)' }}
  />
);

const TodoRow: React.FC<{
  todo: ServiceRequest;
  onOpen: () => void;
}> = ({ todo, onOpen }) => {
  const done = todo.status === 'completed';
  const pending = isPendingReview(todo);
  const dueDate = todoDueDate(todo);
  const fromCarePlan = Boolean(todo.basedOn?.length);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="patient-todo-row"
      className="flex w-full flex-col px-4 py-2.5 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="baseline"
        justify="between"
        className="min-h-6"
      >
        <Text
          inline
          size="sm"
          weight="medium"
          className={done ? 'truncate line-through opacity-60' : 'truncate'}
        >
          {todo.code?.text ?? '(unnamed to-do)'}
        </Text>
        {pending && (
          <Tag pill subtle size="xs" color="insights" label="Unverified" />
        )}
      </Stack>
      <Text size="xs" color="tertiary" weight="regular" className="mt-0.5">
        {[
          done ? 'Done' : undefined,
          dueDate ? `Due ${formatFhirDate(dueDate)}` : undefined,
          fromCarePlan ? 'From care plan' : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>
    </button>
  );
};

export const TodosCard: React.FC<TodosCardProps> = ({
  todos,
  status,
  paging,
  onOpen,
}) => {
  const pendingCount = todos.filter(isPendingReview).length;
  const firstPending = todos.find(isPendingReview);

  return (
    <section
      data-testid="patient-todos-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Patient to-dos
        </Text>
        {pendingCount > 0 && firstPending?.id && (
          <button
            type="button"
            onClick={() => firstPending.id && onOpen(firstPending.id)}
            data-testid="patient-todos-review-pill"
            className="flex items-center gap-1.5 rounded-full border border-border-primary px-2.5 py-0.5 text-xs font-semibold hover:bg-bg-secondary"
          >
            <PendingDot />
            {pendingCount} to review
          </button>
        )}
      </Stack>
      <div className="border-t border-border-primary px-4">
        <SectionBoundary status={status} emptyText="No patient to-dos.">
          <div className="-mx-4">
            {todos.map((todo) => (
              <TodoRow
                key={todo.id}
                todo={todo}
                onOpen={() => todo.id && onOpen(todo.id)}
              />
            ))}
            <LoadMoreRow paging={paging} noun="to-dos" />
          </div>
        </SectionBoundary>
      </div>
    </section>
  );
};
